<!-- Enhanced ModernChatInput with Context Reminder, Starter Questions, and Minimize Button -->
<template>
  <div class="modern-chat-input" :class="{ 'with-active-object': hasActiveObject, 'empty-state': emptyState, 'minimized': isMinimized }">

   <!-- Minimize Button (New) -->
   <div class="minimize-button-container">
     <v-btn
       icon
       small
       class="minimize-btn"
       @click="toggleMinimize"
       :title="isMinimized ? 'Expand' : 'Minimize'"
     >
       <v-icon>{{ isMinimized ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
     </v-btn>
   </div>

   <!-- Active Object Badge (when active). -->
   <div v-if="hasActiveObject && !isMinimized" class="active-object-badge" :class="{ 'enabled': shouldPrependBAO }">
     <v-icon class="badge-icon">{{ chipIcon }}</v-icon>
     <span class="badge-text">Using content from {{ activeObjectLabel }} #{{ objectId }}</span>
     <v-tooltip bottom>
         <template v-slot:activator="{ on, attrs }">
         <v-btn
             icon
             small
             class="badge-toggle"
             @click="toggleBAOPrepend"
             v-bind="attrs"
             v-on="on"
         >
             <v-icon>{{ shouldPrependBAO ? 'mdi-link' : 'mdi-link-off' }}</v-icon>
         </v-btn>
         </template>
         <span>{{ shouldPrependBAO ? 'Focus On' : 'Focus Off' }}</span>
     </v-tooltip>
   </div>

    <!-- Main Input Field Group -->
    <div v-show="!isMinimized" class="input-field-group">
      <!-- Attachment Button -->
      <v-btn icon class="attachment-btn" @click="openFileUploadDialog">
        <v-icon>mdi-paperclip</v-icon>
      </v-btn>

      <!-- Input Field -->
      <div class="input-field-wrapper">
        <!-- Context reminder above the input when typing -->
        <transition name="fade">
          <div
            v-if="showContextReminder"
            class="context-reminder"
            :class="{ 'flashing': isReminderFlashing }"
          >
            <v-icon small :color="strategyColor">{{ ragStrategyIcon }}</v-icon>
            <span>{{ contextReminderMessage }}</span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
              </template>
              <span>{{ contextReminderDisplay }} </span>
            </v-tooltip>
          </div>
        </transition>

        <textarea
          ref="chatInput"
          :value="inputValue"
          @input="handleInput"
          @keydown="handleKeyDown"
          @focus="onInputFocus"
          @blur="onInputBlur"
          class="modern-chat-textarea"
          :class="{ 'typing': isBotTyping, 'input-focused': inputFocused }"
          :placeholder="placeholderText"
        ></textarea>

        <!-- Starter Questions -->
        <div v-show="showSuggestions && filteredSuggestions.length > 0 && emptyState && !isMobile" class="suggestion-chips">
          <div class="suggestion-header">
            <span class="suggestion-label">Try asking:</span>
            <v-btn icon x-small @click="closeSuggestions">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="suggestions-container">
            <v-chip
              v-for="(question, index) in filteredSuggestions"
              :key="index"
              class="suggestion-chip"
              @click="useQuestion(question)"
            >
              <v-icon left small>{{ question.icon }}</v-icon>
              {{ question.text }}
            </v-chip>
          </div>
        </div>
      </div>

      <!-- Additional Controls -->
      <div class="input-controls">
        <!-- Style Toggle Button -->
        <v-btn
          icon
          small
          class="style-toggle-btn"
          @click="toggleMoodSelector"
          :color="showMoodSelector ? 'primary' : ''"
          :title="'Conversation Style'"
        >
          <v-icon>mdi-format-text-variant</v-icon>
        </v-btn>

        <!-- Format Selector Button -->
        <v-btn
          icon
          small
          class="style-toggle-btn"
          @click="toggleFormatSelector"
          :color="showFormatSelector ? 'primary' : ''"
          :title="'Content Format'"
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>

        <!-- Podcast Duration Dropdown -->
        <v-menu offset-y content-class="duration-menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              class="duration-btn"
              :title="'Podcast Duration: ' + selectedLength"
            >
              <v-icon>mdi-clock-outline</v-icon>
              <span class="duration-label">{{ selectedLength }}</span>
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item-group v-model="selectedDurationIndex">
              <v-list-item
                v-for="(duration, i) in availableDurations"
                :key="i"
                @click="selectDuration(duration)"
              >
                <v-list-item-icon>
                  <v-icon v-if="selectedLength === duration">mdi-check</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ duration }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>

        <!-- RAG Strategy Selector (Enhanced) -->
      <v-menu offset-y content-class="rag-menu">
      <template v-slot:activator="{ on, attrs }">
          <v-btn
          icon
          class="rag-strategy-btn"
          v-bind="attrs"
          v-on="on"
          :color="strategyColor"
          :title="'RAG Strategy: ' + currentStrategy"
          >
          <v-icon>{{ ragStrategyIcon }}</v-icon>
          <span class="strategy-label ml-1">{{ getCurrentStrategyText }}</span>
          </v-btn>
      </template>

      <v-list dense>
          <v-list-item
          v-for="strategy in availableStrategies"
          :key="strategy.value"
          @click="onStrategyChange(strategy.value)"
          :class="{'selected-strategy': strategy.value === rag_strategy}"
          >
          <v-list-item-icon>
              <v-icon :color="strategy.value === rag_strategy ? strategyColor : ''">
              {{ strategy.icon }}
              </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
              <v-list-item-title>{{ strategy.text }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="strategy.value === rag_strategy">
              <v-icon color="success">mdi-check</v-icon>
          </v-list-item-action>
          </v-list-item>
      </v-list>
      </v-menu>

        <!-- Send Button -->
        <v-btn
          icon
          color="primary"
          class="send-button"
          @click="isResponseInProgress ? stopResponse() : sendMessage()"
          :disabled="!(inputValue && inputValue.trim && inputValue.trim().length) && !isResponseInProgress"
        >
          <v-icon>
            {{ isResponseInProgress ? 'mdi-stop-circle-outline' : 'mdi-send' }}
          </v-icon>
        </v-btn>
      </div>
    </div>

   <!-- Format Selector Panel (keeps your existing structure) -->
    <div v-show="showFormatSelector && !isMinimized" class="selector-container format-selector">
      <div class="selector-header">
        <span class="selector-title">Choose a content format:</span>
        <v-btn icon x-small @click="toggleFormatSelector">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="selector-options">
        <v-chip
          v-for="(format, index) in availableFormats"
          :key="index"
          :class="{ 'active-selector': selectedFormat === format.value }"
          @click="selectFormat(format.value)"
          class="selector-chip"
        >
          <v-icon left small>{{ format.icon }}</v-icon>
          {{ format.label }}
        </v-chip>
      </div>
    </div>

    <!-- Conversation Style Selector Panel -->
    <div v-show="showMoodSelector && !isMinimized" class="mood-selector-container">
      <div class="mood-selector-header">
        <span class="mood-title">Choose a conversation style:</span>
        <v-btn icon x-small @click="toggleMoodSelector">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="mood-options">
        <v-chip
          v-for="(mood, index) in availableMoods"
          :key="index"
          :class="{ 'active-mood': selectedMood === mood.value }"
          @click="selectMood(mood.value)"
          class="mood-chip"
        >
          <v-icon left small>{{ mood.icon }}</v-icon>
          {{ mood.label }}
        </v-chip>
      </div>
    </div>

  <!-- Combined Indicators in One Line -->
  <div v-show="!isMinimized" class="active-style-indicator">
    <!-- Active Style Indicator -->
    <template v-if="!showMoodSelector">
      <v-icon small color="primary">{{ currentMoodIcon }}</v-icon>
      <span class="active-style-text">{{ currentMoodLabel }} style</span>
    </template>

    <!-- Divider if both are visible -->
    <span class="indicator-divider" v-if="!showMoodSelector && !showFormatSelector">
      |
    </span>

    <!-- Active Format Indicator -->
    <template v-if="!showFormatSelector">
      <v-icon small color="primary">{{ currentFormatIcon }}</v-icon>
      <span class="active-style-text">{{ currentFormatLabel }} format</span>
    </template>
  </div>

  </div>
</template>

<script>
export default {
  props: {
    // Basic props with defaults
    // Starter questions to help new users
    suggestedQuestions: {
      type: Array,
      default: () => []
    },
    isBotTyping: {
      type: Boolean,
      default: false
    },
    segmentSelected: {
      type: Boolean,
      default: false
    },
    isResponseInProgress: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    emptyState: {
      type: Boolean,
      default: false
    },
    // Active object props with defaults
    hasActiveObject: {
      type: Boolean,
      default: false
    },
    activeObject: {
      type: String,
      default: ''
    },
    objectId: {
      default: ''
    },
    shouldPrependBAO: {
      type: Boolean,
      default: false
    },

    // Settings props with defaults
    selectedLength: {
      type: String,
      default: '5min'
    },
    rag_strategy: {
      type: String,
      default: 'web_search'
    },

    // Strategy data with defaults.
    availableStrategies: {
      type: Array,
      default: () => [
        { value: 'llm_passthrough', text: 'LLM', icon: 'mdi-molecule' },
        { value: 'web_search', text: 'Web Search', icon: 'mdi-web' }
      ]
    },
  },

  data() {
    return {
      inputValue: '',
      inputFocused: false,
      showMoodSelector: false,
      showFormatSelector: false,
      selectedMood: 'Conversational',
      selectedFormat: 'podcast',
      availableDurations: ['5min', '10min'],
      selectedDurationIndex: 2,
      isReminderFlashing: false,
      showSuggestions: false,
      isMinimized: false, // New state variable for minimize feature

      availableFormats: [
        { label: 'Conversation', value: 'conversation', icon: 'mdi-chat-outline' },
        { label: 'Podcast', value: 'podcast', icon: 'mdi-microphone' },
        { label: 'Marketing Campaign', value: 'marketing', icon: 'mdi-bullhorn' },
        { label: 'Professional Bio', value: 'bio', icon: 'mdi-account-card-details' },
        { label: 'Corporate Training', value: 'training', icon: 'mdi-school' },
        { label: 'Tech Tutorial', value: 'tutorial', icon: 'mdi-code-tags' }
      ],

      availableMoods: [
      { label: 'Conversational', value: 'Conversational', icon: 'mdi-chat-outline' },
      { label: 'Sarcastic Sage', value: 'Sarcastic Sage', icon: 'mdi-emoticon-cool-outline' },
      { label: 'Authoritarian Analyst', value: 'Authoritarian Analyst', icon: 'mdi-account-tie-outline' },
      { label: 'Comedy King', value: 'Comedy King', icon: 'mdi-emoticon-happy-outline' },
      { label: 'Poetic Philosopher', value: 'Poetic Philosopher', icon: 'mdi-feather' },
      { label: 'Tech Enthusiast', value: 'Tech Enthusiast', icon: 'mdi-laptop' }
      ],
      typingTimer: null,
      lastCursorPosition: 0
    };
  },

  computed: {
    placeholderText() {
      return this.isMobile ? 'Ask anything...' : 'Turn any question into a multi-speaker podcast in seconds...';
    },
    activeObjectLabel() {
      // if activeObject is 'ECCSProject', return 'Planet'
      if (this.activeObject === 'ECCSProject') {
        return 'Planet';
      } else {
        return this.activeObject;
      }
    },
    currentStrategy() {
      const strategy = this.availableStrategies.find(s => s.value === this.rag_strategy);
      return strategy ? strategy.text : 'Select Strategy';
    },

    getCurrentStrategyText() {
      // Return a shorter version of the strategy name for small displays
      const strategy = this.availableStrategies.find(s => s.value === this.rag_strategy);

      if (!strategy) return 'RAG';

      // Use abbreviations for small displays
      const shortNames = {
        'Web Search': 'Web',
        'LLM': 'LLM'
      };

      return shortNames[strategy.text] || strategy.text;
    },

    ragStrategyIcon() {
      const strategy = this.availableStrategies.find(s => s.value === this.rag_strategy);
      return strategy ? strategy.icon : 'mdi-help-circle-outline';
    },

    strategyColor() {
      const colors = {
        web_search: 'success',
        llm_passthrough: 'tips'
      };
      return colors[this.rag_strategy];
    },

    chipIcon() {
      return this.shouldPrependBAO
          ? 'mdi-image-filter-center-strong'
          : 'mdi-image-filter-center-focus-weak';
    },

    currentMoodIcon() {
      const mood = this.availableMoods.find(m => m.value === this.selectedMood);
      return mood ? mood.icon : 'mdi-chat-outline';
    },

    currentFormatIcon() {
      const format = this.availableFormats.find(f => f.value === this.selectedFormat);
      return format ? format.icon : 'mdi-chat-outline';
    },

    currentFormatLabel() {
      const format = this.availableFormats.find(f => f.value === this.selectedFormat);
      return format ? format.label : 'Conversation';
    },

    currentMoodLabel() {
      const mood = this.availableMoods.find(m => m.value === this.selectedMood);
      return mood ? mood.label : 'Conversational';
    },
    contextReminderDisplay() {
      if (this.hasActiveObject && this.shouldPrependBAO) {
        return "Web Search is disabled. Only content from this object will be used. To re-enable Web Search, unselect the object focus.";
      } else if (this.currentStrategy === 'LLM') {
        return "Web Search is disabled. No external sources will be used. To re-enable Web Search, select the Web Search strategy.";
      }
      return "";
    },

    // Context reminder computed properties
    contextReminderMessage() {
      if (this.hasActiveObject && this.shouldPrependBAO) {
        // Customize the message based on object type
        const objectType = this.activeObject === 'ECCSProject' ? 'Planet' : this.activeObject;
        return `Your question will be answered using only ${objectType} #${this.objectId}`;
      } else if (this.currentStrategy === 'LLM') {
        return "Web Search is disabled";
      }
      return '';
    },

    showContextReminder() {
      return (
        (this.hasActiveObject && this.shouldPrependBAO && this.inputValue.trim().length > 0) ||
        (this.currentStrategy === 'LLM' && this.inputValue.trim().length > 0)
      );
    },
    // Filtered suggestions that adapt based on context
    filteredSuggestions() {
      // If we have context-specific suggestions from parent, use those
      if (this.suggestedQuestions && this.suggestedQuestions.length > 0) {
        return this.suggestedQuestions;
      }

      // Otherwise, if we have an active object, show object-specific suggestions
      if (this.hasActiveObject && this.shouldPrependBAO) {
        const objectType = this.activeObject === 'ECCSProject' ? 'Planet' : this.activeObject;
        return [
          { text: `What's in this ${objectType}?`, icon: "mdi-help-circle-outline" },
          { text: `Summarize the content of this ${objectType}`, icon: "mdi-text-box-outline" },
          { text: `What are the key topics in this ${objectType}?`, icon: "mdi-format-list-bulleted" }
        ];
      }

      // Fallback to default questions if no prop was provided
      return [
        { text: "How does music affect our brains?", icon: "mdi-music-note" },
        { text: "What makes a perfect cup of coffee?", icon: "mdi-coffee" },
        { text: "How do different cultures approach time management?", icon: "mdi-clock-outline" },
        { text: "What will transportation look like in 2050?", icon: "mdi-car-electric" },
        { text: "Why do we dream and what do dreams mean?", icon: "mdi-sleep" },
        { text: "How can we effectively learn new skills as adults?", icon: "mdi-brain" }
      ];
    }
  },

  watch: {
   segmentSelected(newVal) {
      if (newVal) {
        this.isMinimized = true; // Minimize when a segment is selected
      }
    },
  },

  methods: {
    // New method for the minimize feature
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;

      // Close any open selectors when minimizing
      if (this.isMinimized) {
        this.showMoodSelector = false;
        this.showFormatSelector = false;
        this.showSuggestions = false;
      }

      // Emit event to let parent components know the state has changed
      this.$emit('minimize-toggled', this.isMinimized);
    },

    closeSuggestions() {
      this.showSuggestions = false;
    },
    handleInput(event) {
      this.inputValue = event.target.value;

      // Hide suggestions when user types
      if (this.inputValue.trim().length > 0) {
        this.showSuggestions = false;
      }

      // Notify parent
      this.$emit('update:input', this.inputValue);

      // Store cursor position for suggestions
      this.lastCursorPosition = event.target.selectionStart;

      // Clear any existing timer
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }

      // Set debounced textarea height adjustment
      this.typingTimer = setTimeout(() => {
        this.adjustTextareaHeight();
      }, 100);

      // Add flash effect to reminder when typing
      if (this.showContextReminder && this.inputValue.trim().length > 10) {
        this.isReminderFlashing = true;
        setTimeout(() => {
          this.isReminderFlashing = false;
        }, 1000);
      }
    },

    handleKeyDown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      } else if (event.key === 'ArrowUp' && !this.inputValue.trim()) {
        // When pressing up with empty input, attempt to edit last message
        event.preventDefault();
        this.$emit('edit-last-message');
      } else if (event.key === '/') {
        // Check for command triggers
        if (this.lastCursorPosition === 0 || this.inputValue[this.lastCursorPosition - 1] === ' ') {
          this.showMoodSelector = true;
        }
      }
    },

    onInputFocus() {
      this.inputFocused = true;
      if (!this.inputValue.trim()) {
        this.showSuggestions = true;
      }
      this.$emit('input-focus');

      // If minimized, expand when focusing
      if (this.isMinimized) {
        this.isMinimized = false;
      }
    },

    onInputBlur() {
      if (document.activeElement !== this.$refs.chatInput) {
        this.inputFocused = false;
        // Don't hide suggestions immediately to allow for clicking them
        setTimeout(() => {
          if (!this.inputFocused) {
            this.showSuggestions = false;
          }
        }, 200);
        this.$emit('input-blur');
      }
    },

    adjustTextareaHeight() {
      const textarea = this.$refs.chatInput;
      if (textarea) {
        // Reset height to get the correct scrollHeight
        textarea.style.height = 'auto';

        // Set new height based on content (with max height constraint applied in CSS)
        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;

        // Emit event for parent components
        this.$emit('height-changed', textarea.scrollHeight);
      }
    },

    toggleBAOPrepend() {
      this.$emit('toggle-bao-prepend');
    },

    openFileUploadDialog() {
      this.$emit('open-file-upload');
    },

    toggleMoodSelector() {
      this.showMoodSelector = !this.showMoodSelector;
    },

    toggleFormatSelector() {
      this.showFormatSelector = !this.showFormatSelector;
    },

    selectFormat(format) {
      this.selectedFormat = format;
      this.showFormatSelector = false;
    },

    selectMood(mood) {
      this.selectedMood = mood;

      // Find the prompt for the selected mood
      const selectedMoodData = this.availableMoods.find(m => m.value === mood);
      if (selectedMoodData && selectedMoodData.prompt) {
        this.$emit('select-mood', mood, selectedMoodData.prompt);
      }

      // Close the mood selector after selection
      this.showMoodSelector = false;
    },

    selectDuration(duration) {
      this.$emit('update-duration', duration);

      // Find index for visual indicator
      const index = this.availableDurations.findIndex(d => d === duration);
      if (index >= 0) {
        this.selectedDurationIndex = index;
      }
    },

    onStrategyChange(strategyValue) {
      this.$emit('update-strategy', strategyValue);
    },

    // Use a suggested question
    useQuestion(question) {
      this.inputValue = question.text;
      this.showSuggestions = false;
      this.$emit('update:input', this.inputValue);
      this.$nextTick(() => {
        this.adjustTextareaHeight();
        this.$refs.chatInput.focus();
      });
    },

    sendMessage() {
      // Safety check for null/undefined value
      if (!this.inputValue || !this.inputValue.trim() || this.isResponseInProgress) return;

      // Add any necessary metadata
      const messageData = {
        text: this.inputValue.trim(),
        mood: this.selectedMood,
        duration: this.selectedLength,
        format: this.selectedFormat
      };

      // Emit the send event with the message data
      this.$emit('send-message', messageData);

      // Clear the input
      this.$emit('input', '');

      // Reset textarea height
      this.$nextTick(() => {
        this.adjustTextareaHeight();
        });

        // clear input
        this.inputValue = '';

        // Always hide suggestions after sending
        this.showSuggestions = false;
      },

      stopResponse() {
        this.$emit('stop-response');
      },

      focusInput() {
        this.$refs.chatInput.focus();
      }
    },

    // Lifecycle hooks
    mounted() {
      // Show suggestions on initial mount but only if not mobile
      this.$nextTick(() => {
        if (!this.isMobile) {
          this.showSuggestions = true;
        }
      });
    }
  }
  </script>

  <style scoped>
  .modern-chat-input {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: rgba(17, 24, 39, 0.8);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 12px 16px;
    transition: all 0.3s ease;
    z-index: 100;
  }

  /* New styles for minimize feature */
  .modern-chat-input.minimized {
    padding: 4px 16px;
    height: 40px; /* Fixed height when minimized */
  }

  .minimize-button-container {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 101;
  }

  .minimize-btn {
    background: rgba(17, 24, 39, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    margin-bottom: -1px;
    transition: all 0.2s ease;
  }

  .theme--light .minimize-btn {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: none;
  }

  .minimize-btn:hover {
    background: rgba(99, 102, 241, 0.1);
  }

  .modern-chat-input.empty-state {
    bottom: 30%;
  }

  @media (max-width: 768px) {
    .modern-chat-input {
      padding: 8px 12px;
    }

    .modern-chat-input.minimized {
      padding: 4px 12px;
    }
  }

  .theme--light .modern-chat-input {
    background: rgba(255, 255, 255, 0.8);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  .modern-chat-input.with-active-object {
    padding-top: 6px;
  }

  .active-object-badge {
    display: flex;
    align-items: center;
    padding: 4px 12px;
    margin-bottom: 8px;
    margin-left: 60px;
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 8px;
    font-size: 0.875rem;
    max-width: max-content;
  }

  .theme--light .active-object-badge {
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
  }

  .active-object-badge.enabled {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.4);
  }

  .badge-icon {
    font-size: 16px;
    margin-right: 8px;
    color: #FF1493;
  }

  .badge-text {
    color: #FF1493;
    font-weight: 500;
    margin-right: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }


  .badge-toggle {
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  .badge-toggle:hover {
    opacity: 1;
  }

  .input-field-group {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 8px 12px;
    transition: all 0.3s ease;
    max-width: 768px;
    margin: 0 auto;
  }

  .theme--light .input-field-group {
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .input-field-group:focus-within {
    border-color: rgb(99, 102, 241);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }

  .attachment-btn {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s ease;
  }

  .theme--light .attachment-btn {
    color: rgba(0, 0, 0, 0.6);
  }

  .attachment-btn:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  .theme--light .attachment-btn:hover {
    color: black;
    background: rgba(0, 0, 0, 0.05);
  }

  .input-field-wrapper {
    flex-grow: 1;
    position: relative;
  }

  .modern-chat-textarea {
    width: 100%;
    border: none;
    background: transparent;
    font-size: 1rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.9);
    resize: none;
    padding: 8px 0;
    min-height: 24px;
    max-height: 200px;
    overflow-y: auto;
    outline: none;
  }

  .theme--light .modern-chat-textarea {
    color: rgba(0, 0, 0, 0.9);
  }

  .modern-chat-textarea::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  .theme--light .modern-chat-textarea::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  .input-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  /* Style toggle button */
  .style-toggle-btn {
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s ease;
    position: relative;
  }

  .theme--light .style-toggle-btn {
    color: rgba(0, 0, 0, 0.6);
  }

  .style-toggle-btn:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  .theme--light .style-toggle-btn:hover {
    color: black;
    background: rgba(0, 0, 0, 0.05);
  }

  .duration-btn {
    position: relative;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s ease;
    margin-right: 4px;
  }

  .theme--light .duration-btn {
    color: rgba(0, 0, 0, 0.6);
  }

  .duration-btn:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  .theme--light .duration-btn:hover {
    color: black;
    background: rgba(0, 0, 0, 0.05);
  }

  .duration-label {
    position: absolute;
    bottom: -6px;
    right: -5px;
    font-size: 8px;
    font-weight: 600;
    background: rgb(99, 102, 241);
    color: white;
    padding: 1px 4px;
    border-radius: 4px;
  }

  /* Enhanced RAG strategy button */
  .rag-strategy-btn {
    min-width: 72px !important;
    height: 32px !important;
    margin-left: 4px;
    transition: all 0.3s ease !important;
  }

  .strategy-label {
    font-size: 0.75rem;
    font-weight: 500;
    margin-left: -4px;
  }

  .selected-strategy {
    background: rgba(99, 102, 241, 0.1);
  }

  .send-button {
    color: rgb(99, 102, 241);
    background: rgba(99, 102, 241, 0.1);
    transition: all 0.3s ease;
  }

  .send-button:hover {
    background: rgba(99, 102, 241, 0.2);
    transform: translateY(-1px);
  }

  .send-button:disabled {
    opacity: 0.5;
    color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  .theme--light .send-button:disabled {
    color: rgba(0, 0, 0, 0.3);
    background: rgba(0, 0, 0, 0.03);
  }

  .mood-selector-container {
    max-width: 768px;
    margin: 8px auto 0;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 8px 12px;
    animation: slideDown 0.3s ease-out;
  }

  .theme--light .mood-selector-container {
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .mood-selector-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .mood-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }

  .theme--light .mood-title {
    color: rgba(0, 0, 0, 0.9);
  }

  .mood-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .mood-chip {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    transition: all 0.2s ease;
  }

  .theme--light .mood-chip {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.8);
  }

  .mood-chip:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  .theme--light .mood-chip:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  .mood-chip.active-mood {
    background: rgba(99, 102, 241, 0.2);
    color: rgb(99, 102, 241);
  }

  .theme--light .mood-chip.active-mood {
    background: rgba(99, 102, 241, 0.1);
    color: rgb(99, 102, 241);
  }

  /* Active Style Indicator */
  .active-style-indicator {
    max-width: 768px;
    margin: 4px auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    font-size: 0.75rem;
    gap: 4px;
  }

  .active-style-text {
    color: rgba(99, 102, 241, 0.9);
    font-weight: 500;
  }

  /* Animation for the mood selector */
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .input-field-group {
      padding: 6px 10px;
    }

    .modern-chat-textarea {
      font-size: 0.875rem;
      padding: 6px 0;
    }

    .badge-text {
      max-width: 120px;
    }

    .rag-strategy-btn {
      min-width: 60px !important;
    }

    .strategy-label {
      font-size: 0.7rem;
    }

    .mood-options {
      gap: 4px;
    }

    .mood-chip {
      font-size: 0.75rem;
    }
    .active-object-badge {
      margin-left: 0;
    }

    .minimize-button-container {
      top: -18px;
    }

    .minimize-btn {
      height: 24px;
      width: 24px;
    }
  }

  @media (max-width: 480px) {
    .badge-text {
      max-width: 80px;
    }

    .rag-strategy-btn {
      min-width: 50px !important;
    }

    .strategy-label {
      display: none;
    }
    .active-object-badge {
      margin-left: 0;
    }
  }

  /* Format Selector Styles - Matching the mood selector exactly */
.format-selector-container {
  max-width: 768px;
  margin: 8px auto 0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  animation: slideDown 0.3s ease-out;
}

.theme--light .format-selector-container {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.format-selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.format-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.theme--light .format-title {
  color: rgba(0, 0, 0, 0.9);
}

.format-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.format-chip {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.theme--light .format-chip {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.8);
}

.format-chip:hover {
  background: rgba(255, 255, 255, 0.15);
}

.theme--light .format-chip:hover {
  background: rgba(0, 0, 0, 0.08);
}

.format-chip.active-format {
  background: rgba(99, 102, 241, 0.2);
  color: rgb(99, 102, 241);
}

.theme--light .format-chip.active-format {
  background: rgba(99, 102, 241, 0.1);
  color: rgb(99, 102, 241);
}

/* Format toggle button */
.format-toggle-btn {
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
  position: relative;
}

.theme--light .format-toggle-btn {
  color: rgba(0, 0, 0, 0.6);
}

.format-toggle-btn:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.theme--light .format-toggle-btn:hover {
  color: black;
  background: rgba(0, 0, 0, 0.05);
}

/* Format indicator label */
.format-indicator {
  position: absolute;
  bottom: -6px;
  right: -5px;
  font-size: 8px;
  font-weight: 600;
  background: rgb(99, 102, 241);
  color: white;
  padding: 1px 4px;
  border-radius: 4px;
}

/* Active Format Indicator */
.active-format-indicator {
  max-width: 768px;
  margin: 4px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  font-size: 0.75rem;
  gap: 4px;
}

.active-format-text {
  color: rgba(99, 102, 241, 0.9);
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .format-options {
    gap: 4px;
  }

  .format-chip {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .format-selector-container {
    padding: 6px 8px;
  }

  .format-title {
    font-size: 0.8rem;
  }

  .format-chip {
    height: 28px !important;
    font-size: 0.7rem;
  }
}

.selector-container {
  max-width: 768px;
  margin: 8px auto 0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  animation: slideDown 0.3s ease-out;
}

.theme--light .selector-container {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.selector-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.theme--light .selector-title {
  color: rgba(0, 0, 0, 0.9);
}

.selector-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selector-chip {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.theme--light .selector-chip {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.8);
}

.selector-chip:hover {
  background: rgba(255, 255, 255, 0.15);
}

.theme--light .selector-chip:hover {
  background: rgba(0, 0, 0, 0.08);
}

.selector-chip.active-selector {
  background: rgba(99, 102, 241, 0.2);
  color: rgb(99, 102, 241);
}

.theme--light .selector-chip.active-selector {
  background: rgba(99, 102, 241, 0.1);
  color: rgb(99, 102, 241);
}

.indicator-divider {
  margin: 0 8px;
  color: rgba(99, 102, 241, 0.6);
}

/* NEW CONTEXT REMINDER STYLES */
.context-reminder {
  position: absolute;
  bottom: calc(100% + 8px);  /* Updated from 6px to 8px for better spacing */
  right: 0;
  background: rgba(255, 105, 180, 0.15);
  border: 1px solid rgba(255, 105, 180, 0.3);
  border-radius: 8px;  /* Slightly more rounded from 6px */
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  z-index: 10;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.9);
}

.theme--light .context-reminder {
  background: rgba(255, 105, 180, 0.1);
  border: 1px solid rgba(255, 105, 180, 0.2);
  color: rgba(0, 0, 0, 0.8);
}

.context-reminder.flashing {
  animation: flash 1s ease;
}

@keyframes flash {
  0% { background-color: rgba(255, 105, 180, 0.15); }
  50% { background-color: rgba(255, 105, 180, 0.25); }  /* Less intense peak */
  100% { background-color: rgba(255, 105, 180, 0.15); }
}

.theme--light .context-reminder.flashing {
  animation: flash-light 1s ease;
}

@keyframes flash-light {
  0% { background-color: rgba(255, 105, 180, 0.1); }
  50% { background-color: rgba(255, 105, 180, 0.2); }
  100% { background-color: rgba(255, 105, 180, 0.1); }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* NEW SUGGESTED QUESTIONS STYLES */
.suggestion-chips {
  /* position: absolute; <- remove this line */
  /* top: calc(100% + 8px); <- remove this line */
  /* left: 0; <- remove this line */
  /* right: 0; <- remove this line */
  max-width: 768px; /* Add this line */
  margin: 8px auto 0; /* Add this line to match the selectors */
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 12px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
  animation: slideDown 0.3s ease-out; /* Add this line to match animation */
}


.theme--light .suggestion-chips {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.suggestion-label {
  font-size: 0.8rem;
  opacity: 0.7;
  margin-bottom: 8px;
  font-weight: 500;
}

.suggestions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.suggestion-chip {
  background: rgba(99, 102, 241, 0.1);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.theme--light .suggestion-chip {
  background: rgba(99, 102, 241, 0.1);
  color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.suggestion-chip:hover {
  background: rgba(99, 102, 241, 0.2);
  transform: translateY(-1px);
}

.theme--light .suggestion-chip:hover {
  background: rgba(99, 102, 241, 0.2);
}

/* Responsive adjustments for all new components */
@media (max-width: 768px) {
  .context-reminder {
    left: 0;
    right: 0;
    font-size: 0.75rem;
    text-align: center;
    white-space: normal;
  }

  .suggestion-chips {
    padding: 8px 10px;
  }

  .suggestion-chip {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .context-reminder {
    padding: 3px 6px;
    font-size: 0.7rem;
  }

  .suggestion-chips {
    padding: 6px 8px;
  }

  .suggestion-chip {
    font-size: 0.75rem;
  }

  .suggestion-label {
    font-size: 0.7rem;
  }
}

.suggestion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
  </style>
